import { NativeSelect } from "../componentes/NativeSelect"
import { Escolha } from "../componentes/Escolha"
import { Input } from "../componentes/Input"
import { useCallback, useEffect, useState } from "react"

import {
  api,
  type AlcanceDeConfiguracoes,
  type EntidadeNaLista,
  type FichaDaEntidade,
  type MudancaDoCadastro,
  type Opcao,
  type SistemaDoCatalogo,
  type SistemaNoPedido,
} from "../api"
import {
  BlocoDeCampos,
  EnviarImagem,
  FormularioDeCampos,
  mensagemDeRecusa,
  useImagemProtegida,
  type CampoDoBloco,
  type ValoresDoBloco,
} from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import * as Icones from "../componentes/Icones"
import { Abas, PainelDaAba, type DescricaoDeAba } from "../componentes/Abas"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { Badge } from "../componentes/Badge"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Checkbox } from "../componentes/Checkbox"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../componentes/DropdownMenu"
import { FiltroAplicado } from "../componentes/FiltroAplicado"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Skeleton } from "../componentes/Skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../componentes/Table"
import {
  Acao,
  Column,
  TablePrincipal2,
  TablePrincipalFiltroGlobal,
} from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { BlocoEditavel } from "../ficha/Campos"
import { ComDica } from "../componentes/ComDica"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { UFS, data, dataHora, inteiro } from "../formato"
import type { ItemDeConfiguracao } from "../itens-de-configuracao"
import {
  ABAS_NO_CARTAO,
  ATALHO,
  BOTAO_BRANCO,
  BOTAO_CANCELAR,
  CABECA,
  CABECALHO_DO_SHEET,
  CARTAO_DAS_ABAS,
  CELULA_DENSA,
  COL_DETALHE,
  COL_OBJETO,
  CORPO_LISTRADO,
  EM_PILHA,
  FAZER,
  LADO_A_LADO,
  MAIUSCULA,
  PROCEDENCIA,
  RECUSA,
  SEGUINTE_NA_PILHA,
  SEGUNDO_LADO,
  TABELA,
  VAZIO,
} from "../lib/classes"
import { cn } from "../lib/cn"

type PropsDaTela = {
  alcance: AlcanceDeConfiguracoes
  ativo?: boolean
  tipo?: string
  aoFiltrarTipo?: (tipo: string | null) => void
  sistema?: string
  aoFiltrarSistema?: (sistema: string | null) => void
  entidade?: number
  aoAbrirEntidade: (id: number | null, substituir?: boolean) => void
  aoSaberDaEntidade?: (sigla: string | null) => void
  aoAbrirConfiguracao?: (item: ItemDeConfiguracao) => void
}

type SituacaoDaLicenca = "vigente" | "vencida" | "nao-informada"

type Filtros = { tipo: string; situacao: string; licenca: string }

const SEM_FILTRO: Filtros = { tipo: "", situacao: "", licenca: "" }

const POR_PAGINA = 20

const FILTRO_DA_BARRA = "w-full min-w-0 md:w-44 md:flex-none"

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()

const hoje = () => new Date().toISOString().slice(0, 10)

function situacaoDaLicenca(entidade: EntidadeNaLista): SituacaoDaLicenca {
  if (entidade.licenca_fim === null) return "nao-informada"
  return entidade.licenca_fim < hoje() ? "vencida" : "vigente"
}

function casaComABusca(entidade: EntidadeNaLista, busca: string): boolean {
  const procurado = normalizar(busca)
  if (procurado === "") return true
  const documento = procurado.replace(/[^0-9a-z]/g, "")
  const textos = [entidade.nome, entidade.sigla, entidade.nome_exibicao ?? "", entidade.municipio ?? ""].map(normalizar)
  if (textos.some((texto) => texto.includes(procurado))) return true
  const cnpj = normalizar(entidade.cnpj ?? "").replace(/[^0-9a-z]/g, "")
  return documento !== "" && cnpj.includes(documento)
}

function podeMudarSituacao(podeCriar: boolean, alcance: AlcanceDeConfiguracoes, entidade: { is_demo: boolean }): boolean {
  return podeCriar && (entidade.is_demo || !alcance.conta_de_demonstracao)
}

export function Entidades({
  alcance,
  ativo = true,
  tipo,
  aoFiltrarTipo,
  sistema,
  aoFiltrarSistema,
  entidade,
  aoAbrirEntidade,
  aoSaberDaEntidade,
  aoAbrirConfiguracao,
}: PropsDaTela) {
  const [lista, definirLista] = useState<EntidadeNaLista[] | null>(null)
  const [podeCriar, definirPodeCriar] = useState(false)
  const [opcoes, definirOpcoes] = useState<{ tipos: Opcao[]; poderes: Opcao[]; esferas: Opcao[] }>({
    tipos: [],
    poderes: [],
    esferas: [],
  })
  const [erro, definirErro] = useState<string | null>(null)
  const [busca, definirBusca] = useState("")
  const [filtros, definirFiltros] = useState<Filtros>({ ...SEM_FILTRO, tipo: tipo ?? "" })
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [criando, definirCriando] = useState(false)
  const [desativando, definirDesativando] = useState<EntidadeNaLista | null>(null)

  const carregar = useCallback(() => {
    definirErro(null)
    api
      .entidades("")
      .then((resposta) => {
        definirLista(resposta.dados)
        definirPodeCriar(resposta.pode_criar)
        definirOpcoes({ tipos: resposta.tipos, poderes: resposta.poderes, esferas: resposta.esferas })
      })
      .catch((falha: unknown) => definirErro(mensagemDeRecusa(falha)))
  }, [])

  useEffect(() => {
    if (entidade === undefined) carregar()
  }, [entidade])

  useEffect(() => {
    definirFiltros((atuais) => (atuais.tipo === (tipo ?? "") ? atuais : { ...atuais, tipo: tipo ?? "" }))
  }, [tipo])

  useEffect(() => {
    if (!ativo || entidade !== undefined || podeCriar || lista === null || lista.length !== 1) return
    const unica = lista[0]
    if (unica !== undefined) aoAbrirEntidade(unica.id, true)
  }, [ativo, entidade, podeCriar, lista])

  if (entidade !== undefined) {
    return (
      <FichaDaEntidadeNaTela
        key={entidade}
        id={entidade}
        aoVoltar={podeCriar || lista === null || lista.length > 1 ? () => aoAbrirEntidade(null) : null}
        aoSaberDaEntidade={aoSaberDaEntidade}
        aoAbrirConfiguracao={aoAbrirConfiguracao}
      />
    )
  }

  if (erro !== null) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
            {erro}
          </p>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
            <Icones.Recalcular />
            Tentar de novo
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (lista === null) {
    return (
      <Card aria-busy="true">
        <CardHeader>
          <CardTitle>Entidades</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Skeleton className="h-10 w-1/2" />
          {[0, 1, 2, 3, 4].map((linha) => (
            <Skeleton key={linha} className="h-9 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const mudarSituacao = (alvo: EntidadeNaLista, ativa: boolean) => {
    api
      .mudarSituacaoDaEntidade(alvo.id, ativa)
      .then(() => {
        toast.success(`Entidade ${alvo.sigla} ${ativa ? "reativada" : "desativada"}`)
        carregar()
      })
      .catch((falha: unknown) => toast.error(mensagemDeRecusa(falha)))
  }

  const filtradas = lista.filter(
    (e) =>
      casaComABusca(e, busca) &&
      (filtros.tipo === "" || e.tipo_rotulo === filtros.tipo) &&
      (filtros.situacao === "" || (filtros.situacao === "ativa") === e.ativa) &&
      (filtros.licenca === "" || situacaoDaLicenca(e) === filtros.licenca) &&
      (sistema === undefined || e.sistemas.includes(sistema)),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtradas.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtradas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const tipos = [...new Set(lista.map((e) => e.tipo_rotulo).filter((t): t is string => t !== null))].sort()
  const filtrar = (novos: Filtros) => {
    if (novos.tipo !== filtros.tipo) aoFiltrarTipo?.(novos.tipo === "" ? null : novos.tipo)
    definirFiltros(novos)
    definirPagina(1)
  }
  const comFiltro = filtros.tipo !== "" || filtros.situacao !== "" || filtros.licenca !== ""

  const cadastrar = (
    <Button type="button" className="h-10" onClick={() => definirCriando(true)}>
      <Icones.Mais />
      Cadastrar entidade
    </Button>
  )

  const vazio =
    lista.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Orgao />
          </EmptyMedia>
          <EmptyTitle>Nenhuma entidade cadastrada</EmptyTitle>
          <EmptyDescription>
            {podeCriar ? "Cadastre a primeira entidade atendida pelo sistema." : "O Root cadastra a primeira entidade."}
          </EmptyDescription>
        </EmptyHeader>
        {podeCriar && <EmptyContent>{cadastrar}</EmptyContent>}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nenhuma entidade encontrada</EmptyTitle>
          <EmptyDescription>
            {busca !== "" ? `Nada casa com "${busca}"${comFiltro ? " nos filtros escolhidos" : ""}.` : "Nada casa com os filtros escolhidos."}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {busca !== "" && (
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
              Limpar a busca
            </Button>
          )}
          {comFiltro && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={BOTAO_BRANCO}
              onClick={() => filtrar(SEM_FILTRO)}
            >
              Limpar filtros
            </Button>
          )}
        </EmptyContent>
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entidades</CardTitle>
      </CardHeader>
      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtradas.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          filtrosNaBarra={
            <>
              {sistema !== undefined && <FiltroAplicado rotulo="Sistema" valor={sistema} aoLimpar={() => aoFiltrarSistema?.(null)} />}
              <FiltroDaLista
                rotulo="Filtrar pelo tipo"
                vazio="Todos os tipos"
                valor={filtros.tipo}
                aoMudar={(tipo) => filtrar({ ...filtros, tipo })}
                opcoes={tipos.map((t) => ({ chave: t, rotulo: t }))}
              />
              <FiltroDaLista
                rotulo="Filtrar pela situação"
                vazio="Todas as situações"
                valor={filtros.situacao}
                aoMudar={(situacao) => filtrar({ ...filtros, situacao })}
                opcoes={[
                  { chave: "ativa", rotulo: "Ativa" },
                  { chave: "inativa", rotulo: "Inativa" },
                ]}
              />
              <FiltroDaLista
                rotulo="Filtrar pela licença"
                vazio="Todas as licenças"
                valor={filtros.licenca}
                aoMudar={(licenca) => filtrar({ ...filtros, licenca })}
                opcoes={[
                  { chave: "vigente", rotulo: "Vigente" },
                  { chave: "vencida", rotulo: "Vencida" },
                  { chave: "nao-informada", rotulo: "Não informada" },
                ]}
              />
            </>
          }
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={podeCriar && cadastrar}
          aoClicarNaLinha={(linha) => aoAbrirEntidade(linha.id)}
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome, sigla, município ou CNPJ" />
          <Column<EntidadeNaLista>
            name="sigla"
            label="Sigla"
            enableSorting={false}
            render={(_valor, linha) => (
              <span className="inline-flex items-center gap-2">
                <BrasaoDaEntidade id={linha.id} nome={linha.nome} versao={linha.brasao_enviado_em} />
                <strong>{linha.sigla}</strong>
              </span>
            )}
          />
          <Column<EntidadeNaLista>
            name="nome"
            label="Nome"
            enableSorting={false}
            render={(_valor, linha) => (
              <span className="inline-flex flex-wrap items-center gap-2 text-(--tinta-2)">
                {linha.nome}
                {linha.is_demo && <Badge tom="cinza">Demonstração</Badge>}
              </span>
            )}
          />
          <Column<EntidadeNaLista>
            name="cnpj"
            label="CNPJ"
            enableSorting={false}
            render={(valor) => <span className="tabular-nums">{valor === null ? "—" : String(valor)}</span>}
          />
          <Column<EntidadeNaLista> name="tipo_rotulo" label="Tipo" enableSorting={false} render={(valor) => (valor === null ? "—" : String(valor))} />
          <Column<EntidadeNaLista>
            name="municipio"
            label="Município"
            enableSorting={false}
            render={(_valor, linha) => (linha.municipio === null ? "—" : `${linha.municipio}${linha.uf ? `/${linha.uf}` : ""}`)}
          />
          <Column<EntidadeNaLista>
            name="licenca_fim"
            label="Licença"
            enableSorting={false}
            render={(_valor, linha) => <SeloDaLicenca entidade={linha} />}
          />
          <Column<EntidadeNaLista>
            name="ativa"
            label="Situação"
            enableSorting={false}
            render={(_valor, linha) => <Selo situacao={linha.ativa ? "ativa" : "inativa"} />}
          />
          <Acao<EntidadeNaLista> label="Abrir" icon={Icones.Abrir} onClick={(linha) => aoAbrirEntidade(linha.id)} />
          <Acao<EntidadeNaLista>
            label="Desativar"
            icon={Icones.Dispensar}
            visivel={(linha) => linha.ativa && podeMudarSituacao(podeCriar, alcance, linha)}
            onClick={definirDesativando}
          />
          <Acao<EntidadeNaLista>
            label="Reativar"
            icon={Icones.Reabrir}
            visivel={(linha) => !linha.ativa && podeMudarSituacao(podeCriar, alcance, linha)}
            onClick={(linha) => mudarSituacao(linha, true)}
          />
        </TablePrincipal2>
      </CardContent>

      <ConfirmarDesativacao
        sigla={desativando?.sigla ?? null}
        aoCancelar={() => definirDesativando(null)}
        aoConfirmar={() => {
          if (desativando !== null) mudarSituacao(desativando, false)
          definirDesativando(null)
        }}
      />

      <Sheet open={criando} onOpenChange={definirCriando}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-xl [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">Nova entidade</SheetTitle>
          </SheetHeader>
          {criando && (
            <div className="px-6 pt-3 pb-6">
              <NovaEntidade
                opcoes={opcoes}
                aoCancelar={() => definirCriando(false)}
                aoCriar={(ficha) => {
                  definirCriando(false)
                  toast.success(`Entidade ${ficha.sigla} cadastrada`)
                  aoAbrirEntidade(ficha.id)
                }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function FiltroDaLista({
  rotulo,
  vazio,
  valor,
  aoMudar,
  opcoes,
}: {
  rotulo: string
  vazio: string
  valor: string
  aoMudar: (valor: string) => void
  opcoes: Opcao[]
}) {
  return (
    <Escolha
      rotulo={rotulo}
      classeDaCaixa={FILTRO_DA_BARRA}
      valor={valor}
      aoMudar={aoMudar}
      vazio={vazio}
      opcoes={opcoes.map((opcao) => ({ valor: opcao.chave, rotulo: opcao.rotulo }))}
    />
  )
}

function SeloDaLicenca({ entidade }: { entidade: EntidadeNaLista }) {
  const situacao = situacaoDaLicenca(entidade)
  if (situacao === "nao-informada") {
    return <span className="text-(--tinta-3)">Não informada</span>
  }
  const modalidade = entidade.licenca_modalidade_rotulo === null ? "" : ` · ${entidade.licenca_modalidade_rotulo}`
  const dica =
    situacao === "vencida" ? `Licença vencida em ${data(entidade.licenca_fim)}${modalidade}` : `Licença até ${data(entidade.licenca_fim)}${modalidade}`
  return <Selo situacao={situacao} titulo={dica} />
}

function BrasaoDaEntidade({
  id,
  nome,
  versao,
  grande = false,
}: {
  id: number
  nome: string
  versao: string | null
  grande?: boolean
}) {
  const { endereco } = useImagemProtegida(
    () => (versao !== null ? api.brasao(id) : Promise.resolve(null)),
    `${id}-${versao ?? ""}`,
  )
  if (endereco === null) return null
  return (
    <img
      data-slot="brasao-da-entidade"
      className={cn(
        "flex-none rounded-(--raio-controle) border border-(--borda) bg-(--branco) object-contain p-0.5",
        grande ? "h-12 w-auto max-w-24" : "size-7",
      )}
      src={endereco}
      alt={`Brasão de ${nome}`}
    />
  )
}

function ConfirmarDesativacao({
  sigla,
  aoCancelar,
  aoConfirmar,
}: {
  sigla: string | null
  aoCancelar: () => void
  aoConfirmar: () => void
}) {
  return (
    <AlertDialog open={sigla !== null} onOpenChange={(aberto) => !aberto && aoCancelar()}>
      <AlertDialogContent>
        <AlertDialogHeader tom="perigo">
          <AlertDialogTitle>Desativar a entidade {sigla}?</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <AlertDialogDescription>
            As contas dela passam a só consultar: nada mais pode ser salvo, e o processamento diário e os avisos param. Nada se
            apaga: o histórico fica guardado, e é possível reativar depois.
          </AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button type="button" variant="outline">
              <Icones.Fechar />
              Cancelar
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button type="button" variant="destructive" onClick={aoConfirmar}>
              <Icones.Dispensar />
              Desativar
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function NovaEntidade({
  opcoes,
  aoCriar,
  aoCancelar,
}: {
  opcoes: { tipos: Opcao[]; poderes: Opcao[]; esferas: Opcao[] }
  aoCriar: (ficha: FichaDaEntidade) => void
  aoCancelar: () => void
}) {
  const campos: CampoDoBloco[] = [
    { campo: "nome", rotulo: "Nome oficial", largo: true, obrigatorio: true },
    { campo: "sigla", rotulo: "Sigla", obrigatorio: true },
    { campo: "cnpj", rotulo: "CNPJ", tipo: "cnpj" },
    { campo: "tipo", rotulo: "Tipo de entidade", tipo: "escolha", opcoes: opcoes.tipos },
    { campo: "poder", rotulo: "Poder", tipo: "escolha", opcoes: opcoes.poderes },
    { campo: "esfera", rotulo: "Esfera", tipo: "escolha", opcoes: opcoes.esferas },
  ]
  const [valores, definirValores] = useState<ValoresDoBloco>({})
  const [setorNome, definirSetorNome] = useState("")
  const [setorSigla, definirSetorSigla] = useState("")
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [enviando, definirEnviando] = useState(false)
  const falta = faltaNoFormulario([
    ["o nome oficial", valores["nome"]],
    ["a sigla", valores["sigla"]],
    ["o nome do setor", setorNome],
  ])

  const enviar = () => {
    definirEnviando(true)
    definirRecusa(null)
    const pedido: MudancaDoCadastro = {}
    for (const { campo } of campos) {
      const valor = valores[campo]
      if (valor !== undefined && valor !== null && valor !== "") pedido[campo] = valor
    }
    pedido["setor_nome"] = setorNome
    if (setorSigla.trim() !== "") pedido["setor_sigla"] = setorSigla
    api
      .criarEntidade(pedido)
      .then(aoCriar)
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
      .finally(() => definirEnviando(false))
  }

  return (
    <>
      <FormularioDeCampos prefixo="nova-entidade" campos={campos} inicial={{}} aoMudar={definirValores} naGaveta />
      <fieldset className="mt-4 border-t border-(--borda) pt-3">
        <legend className="pr-2 text-(length:--texto-sm) font-semibold text-(--tinta)">Setor de vínculo inicial</legend>
        <p className={PROCEDENCIA}>O primeiro setor da entidade, onde a primeira conta será lotada. Outros setores se criam depois, em Setores.</p>
        <div className="mt-3 flex flex-wrap gap-3">
          <div className="flex-[2_1_240px]">
            <RotuloDoCampo htmlFor="nova-entidade-setor-nome" obrigatorio>
              Nome do setor
            </RotuloDoCampo>
            <Input
              id="nova-entidade-setor-nome"
              required
              aria-required="true"
              autoComplete="off"
              maxLength={100}
              value={setorNome}
              onChange={(evento) => definirSetorNome(evento.target.value)}
            />
          </div>
          <div className="flex-[1_1_140px]">
            <RotuloDoCampo htmlFor="nova-entidade-setor-sigla">Sigla do setor</RotuloDoCampo>
            <Input
              id="nova-entidade-setor-sigla"
              autoComplete="off"
              maxLength={45}
              value={setorSigla}
              onChange={(evento) => definirSetorSigla(evento.target.value)}
            />
          </div>
        </div>
      </fieldset>
      {recusa !== null && (
        <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
          {recusa}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button title={falta ?? undefined} type="button" disabled={enviando || falta !== null} onClick={enviar}>
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </>
  )
}

type AbaDaEntidade = "identificacao" | "endereco" | "representacao" | "licenca"

export function FichaDaEntidadeNaTela({
  id,
  aoVoltar,
  aoSaberDaEntidade,
  aoAbrirConfiguracao,
}: {
  id: number
  aoVoltar: (() => void) | null
  aoSaberDaEntidade?: (sigla: string | null) => void
  aoAbrirConfiguracao?: (item: ItemDeConfiguracao) => void
}) {
  const [ficha, definirFicha] = useState<FichaDaEntidade | null>(null)
  const [erro, definirErro] = useState<string | null>(null)
  const [aba, definirAba] = useState<AbaDaEntidade>("identificacao")

  useEffect(() => {
    api
      .entidade(id)
      .then(definirFicha)
      .catch((falha: unknown) => definirErro(mensagemDeRecusa(falha)))
  }, [id])

  useEffect(() => {
    if (ficha !== null) aoSaberDaEntidade?.(ficha.sigla)
  }, [ficha?.sigla, aoSaberDaEntidade])

  if (erro !== null) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
            {erro}
          </p>
          {aoVoltar !== null && (
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={aoVoltar}>
              <Icones.Voltar />
              Voltar à lista
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }
  if (ficha === null) {
    return (
      <Card aria-busy="true">
        <CardContent className="flex items-center gap-4">
          <Skeleton className="size-14" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const gravar = async (mudanca: MudancaDoCadastro) => {
    definirFicha(await api.alterarEntidade(ficha.id, mudanca))
  }
  const motivoDoCadastro = "só o gestor desta entidade ou o Root altera"
  const valores = ficha as unknown as ValoresDoBloco

  const blocoDeCampos = (prefixo: string, titulo: string, campos: CampoDoBloco[], recado?: string) => (
    <BlocoDeCampos
      prefixo={`entidade-${prefixo}`}
      titulo={titulo}
      recado={recado}
      campos={campos}
      valores={valores}
      editavel={ficha.pode_editar_cadastro}
      motivoSemEdicao={motivoDoCadastro}
      aoGravar={gravar}
    />
  )

  const abas: DescricaoDeAba<AbaDaEntidade>[] = [
    { chave: "identificacao", rotulo: "Identificação" },
    { chave: "endereco", rotulo: "Endereço e contato" },
    { chave: "representacao", rotulo: "Representação e controle" },
    {
      chave: "licenca",
      rotulo: "Licença e sistemas",
      contagem: ficha.licenca_modalidade === "integrado" ? ficha.sistemas.length : null,
    },
  ]

  return (
    <>
      <CabecaDaEntidade ficha={ficha} aoVoltar={aoVoltar} aoMudar={definirFicha} />
      <div data-slot="cartao-das-abas" className={CARTAO_DAS_ABAS}>
        <Abas nome="entidade" classe={ABAS_NO_CARTAO} rotulo="Seções do cadastro da entidade" abas={abas} atual={aba} aoTrocar={definirAba} />

        <PainelDaAba nome="entidade" chave="identificacao" ativa={aba === "identificacao"}>
          <div className={LADO_A_LADO}>
            <div>
              {blocoDeCampos("identificacao", "Identificação", [
                { campo: "nome", rotulo: "Nome oficial", obrigatorio: true },
                { campo: "nome_exibicao", rotulo: "Nome de exibição" },
                { campo: "sigla", rotulo: "Sigla", obrigatorio: true },
                { campo: "cnpj", rotulo: "CNPJ", tipo: "cnpj" },
                { campo: "tipo", rotulo: "Tipo", tipo: "escolha", opcoes: ficha.tipos },
                { campo: "poder", rotulo: "Poder", tipo: "escolha", opcoes: ficha.poderes },
                { campo: "esfera", rotulo: "Esfera", tipo: "escolha", opcoes: ficha.esferas },
                { campo: "natureza_juridica", rotulo: "Natureza jurídica" },
              ])}
            </div>
            <div className={SEGUNDO_LADO}>
              <BlocoDoBrasao ficha={ficha} aoMudar={() => void api.entidade(ficha.id).then(definirFicha)} />
            </div>
          </div>
        </PainelDaAba>

        <PainelDaAba nome="entidade" chave="endereco" ativa={aba === "endereco"}>
          <div className={LADO_A_LADO}>
            <div>
              {blocoDeCampos("localizacao", "Endereço", [
                { campo: "cep", rotulo: "CEP", tipo: "cep" },
                { campo: "logradouro", rotulo: "Logradouro" },
                { campo: "numero_do_endereco", rotulo: "Número" },
                { campo: "complemento", rotulo: "Complemento" },
                { campo: "bairro", rotulo: "Bairro" },
                { campo: "municipio", rotulo: "Município" },
                { campo: "uf", rotulo: "UF", tipo: "escolha", opcoes: UFS },
                { campo: "codigo_ibge", rotulo: "Código IBGE" },
              ])}
            </div>
            <div className={SEGUNDO_LADO}>
              {blocoDeCampos("contato", "Contato", [
                { campo: "telefone", rotulo: "Telefone", tipo: "telefone" },
                { campo: "email_institucional", rotulo: "E-mail institucional", tipo: "email" },
                { campo: "site", rotulo: "Site", tipo: "endereco" },
              ])}
            </div>
          </div>
        </PainelDaAba>

        <PainelDaAba nome="entidade" chave="representacao" ativa={aba === "representacao"}>
          <div className={LADO_A_LADO}>
            <div>
              {blocoDeCampos(
                "representacao",
                "Representação legal",
                [
                  { campo: "representante_nome", rotulo: "Representante legal" },
                  { campo: "representante_cargo", rotulo: "Cargo" },
                  { campo: "responsavel_email", rotulo: "E-mail do responsável", tipo: "email" },
                  { campo: "responsavel_telefone", rotulo: "Telefone do responsável", tipo: "telefone" },
                  { campo: "representante_cpf", rotulo: "CPF", tipo: "cpf" },
                  { campo: "representante_mandato_inicio", rotulo: "Início do mandato", tipo: "data" },
                  { campo: "representante_mandato_fim", rotulo: "Fim do mandato", tipo: "data" },
                ],
                ficha.representante_cpf_mascarado ? "CPF mascarado para quem não administra a entidade." : undefined,
              )}
            </div>
            <div className={SEGUNDO_LADO}>
              {blocoDeCampos("controle", "Controle externo", [
                { campo: "tce_nome", rotulo: "Tribunal de Contas" },
                { campo: "tce_codigo_da_unidade", rotulo: "Código da unidade" },
              ])}
            </div>
          </div>
        </PainelDaAba>

        <PainelDaAba nome="entidade" chave="licenca" ativa={aba === "licenca"}>
          <BlocosDoLicenciamento
            ficha={ficha}
            aoGravar={gravar}
            aoTrocarSistemas={() => void api.entidade(ficha.id).then(definirFicha)}
            aoAbrirPrazos={aoAbrirConfiguracao === undefined ? undefined : () => aoAbrirConfiguracao("parametros")}
          />
        </PainelDaAba>
      </div>
    </>
  )
}

function CabecaDaEntidade({
  ficha,
  aoVoltar,
  aoMudar,
}: {
  ficha: FichaDaEntidade
  aoVoltar: (() => void) | null
  aoMudar: (ficha: FichaDaEntidade) => void
}) {
  const [confirmando, definirConfirmando] = useState(false)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const mudar = (ativa: boolean) => {
    definirRecusa(null)
    api
      .mudarSituacaoDaEntidade(ficha.id, ativa)
      .then((nova) => {
        aoMudar(nova)
        toast.success(`Entidade ${ficha.sigla} ${ativa ? "reativada" : "desativada"}`)
      })
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
  }
  return (
    <Card data-parte="cabeca-da-entidade">
      <CardHeader>
        <CardTitle>{ficha.sigla}</CardTitle>
        {(aoVoltar !== null || ficha.pode_mudar_situacao) && (
          <CardAction>
            <div className="flex flex-wrap items-center gap-2">
              {aoVoltar !== null && (
                <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={aoVoltar}>
                  <Icones.Voltar />
                  Voltar à lista
                </Button>
              )}
              {ficha.pode_mudar_situacao && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={cn("size-8", BOTAO_BRANCO)}
                      aria-label="Mais ações"
                      title="Mais ações"
                    >
                      <Icones.MaisAcoes tamanho={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {ficha.ativa ? (
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => definirConfirmando(true)}>
                        <Icones.Dispensar />
                        Desativar entidade
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => mudar(true)}>
                        <Icones.Reabrir />
                        Reativar entidade
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex items-center gap-4">
        <BrasaoDaEntidade
          id={ficha.id}
          nome={ficha.nome}
          versao={ficha.brasao.existe ? (ficha.brasao.enviado_em ?? "existe") : null}
          grande
        />
        <div className="min-w-0">
          <p className={cn("font-semibold text-(--tinta)", MAIUSCULA)}>{ficha.nome}</p>
          <p className={PROCEDENCIA}>
            {ficha.cnpj !== null ? `CNPJ ${ficha.cnpj}` : "CNPJ não informado"}
            {ficha.municipio !== null && ` · ${ficha.municipio}${ficha.uf ? `/${ficha.uf}` : ""}`}
          </p>
          <p className="mt-3 flex flex-wrap items-center gap-2">
            <Selo situacao={ficha.ativa ? "ativa" : "inativa"} />
            {ficha.is_demo && <Badge tom="cinza">Demonstração</Badge>}
            {!ficha.ativa && ficha.desativada_em !== null && (
              <span className={PROCEDENCIA}>{`Desativada em ${data(ficha.desativada_em.slice(0, 10))}`}</span>
            )}
            {ficha.licenca_situacao === "vencida" && <Selo situacao="vencida">{`Licença vencida em ${data(ficha.licenca_fim)}`}</Selo>}
            {ficha.licenca_situacao === "vigente" && <Selo situacao="vigente">{`Licença até ${data(ficha.licenca_fim)}`}</Selo>}
          </p>
        </div>
      </CardContent>
      {recusa !== null && (
        <CardContent>
          <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
            {recusa}
          </p>
        </CardContent>
      )}
      <ConfirmarDesativacao
        sigla={confirmando ? ficha.sigla : null}
        aoCancelar={() => definirConfirmando(false)}
        aoConfirmar={() => {
          definirConfirmando(false)
          mudar(false)
        }}
      />
    </Card>
  )
}

function BlocoDoBrasao({ ficha, aoMudar }: { ficha: FichaDaEntidade; aoMudar: () => void }) {
  const [aviso, definirAviso] = useState<string | null>(null)
  const [recusa, definirRecusa] = useState<string | null>(null)
  const { endereco, carregando } = useImagemProtegida(
    () => (ficha.brasao.existe ? api.brasao(ficha.id) : Promise.resolve(null)),
    `${ficha.id}-${ficha.brasao.enviado_em ?? ""}`,
  )

  return (
    <BlocoEditavel
      titulo="Brasão ou logotipo"
      recado="PNG ou JPEG. Sai no cabeçalho do laudo."
      editavel={ficha.pode_editar_cadastro}
      motivoSemEdicao="só o gestor desta entidade ou o Root envia"
      acao={
        ficha.pode_editar_cadastro ? (
          <EnviarImagem
            rotulo={ficha.brasao.existe ? "Trocar a imagem" : "Enviar imagem"}
            nome="brasao-da-entidade"
            aoEnviar={async (arquivo) => {
              definirAviso(null)
              definirRecusa(null)
              try {
                const resposta = await api.enviarBrasao(ficha.id, arquivo)
                definirAviso(resposta.alerta)
                aoMudar()
              } catch (falha: unknown) {
                definirRecusa(mensagemDeRecusa(falha))
              }
            }}
          />
        ) : undefined
      }
      leitura={
        <>
          {recusa !== null && <p className={cn(RECUSA, MAIUSCULA)}>{recusa}</p>}
          {aviso !== null && <p className={cn(FAZER, "text-(length:--texto-sm)", MAIUSCULA)}>{aviso}</p>}
          {endereco !== null ? (
            <div className="flex flex-col items-start gap-2">
              <img
                className="max-h-40 max-w-60 rounded-(--raio-controle) border border-(--borda) bg-(--branco) object-contain p-2"
                src={endereco}
                alt={`Brasão de ${ficha.nome}`}
              />
              <p className={PROCEDENCIA}>
                {ficha.brasao.tipo === "image/png" ? "PNG" : "JPEG"} ·{" "}
                {ficha.brasao.tamanho === null ? "—" : `${Math.ceil(ficha.brasao.tamanho / 1024)} KB`} · enviado em{" "}
                {dataHora(ficha.brasao.enviado_em)}
              </p>
            </div>
          ) : (
            <p className={VAZIO}>{carregando && ficha.brasao.existe ? "Abrindo a imagem..." : "Nenhuma imagem enviada."}</p>
          )}
        </>
      }
    />
  )
}

const GRADE_DA_LICENCA = {
  leitura: "flex flex-col",
  formulario: "grid grid-cols-1 items-start gap-3 md:grid-cols-2 md:[&>.basis-full]:col-span-2",
}

function BlocosDoLicenciamento({
  ficha,
  aoGravar,
  aoTrocarSistemas,
  aoAbrirPrazos,
}: {
  ficha: FichaDaEntidade
  aoGravar: (mudanca: MudancaDoCadastro) => Promise<void>
  aoTrocarSistemas: () => void
  aoAbrirPrazos?: () => void
}) {
  const motivo = "só o Root altera o licenciamento"
  const integrado = ficha.licenca_modalidade === "integrado"
  const linkDosPrazos =
    aoAbrirPrazos === undefined ? (
      "Limites legais e prazos"
    ) : (
      <a
        href="/configuracoes/parametros"
        className={cn(ATALHO, "inline")}
        onClick={(evento) => {
          evento.preventDefault()
          aoAbrirPrazos()
        }}
      >
        Limites legais e prazos
      </a>
    )

  return (
    <div className={EM_PILHA}>
      <div>
        <BlocoDeCampos
          prefixo="entidade-licenca"
          titulo="Licenciamento e integração"
          recado={
            ficha.licenca_modalidade === null
              ? "Ainda não se informou se o sistema foi licenciado sozinho ou integrado a outros sistemas DATAFull."
              : undefined
          }
          campos={[
            { campo: "licenca_modalidade", rotulo: "Modalidade", tipo: "escolha", opcoes: ficha.modalidades },
            { campo: "licenca_contrato", rotulo: "Contrato ou processo de licenciamento" },
            { campo: "licenca_inicio", rotulo: "Início da licença", tipo: "data" },
            { campo: "licenca_fim", rotulo: "Término da licença", tipo: "data", naoAntesDe: "licenca_inicio" },
            { campo: "licenca_observacao", rotulo: "Observação", largo: true },
          ]}
          grade={GRADE_DA_LICENCA}
          valores={ficha as unknown as ValoresDoBloco}
          editavel={ficha.pode_editar_licenciamento}
          motivoSemEdicao={motivo}
          aoGravar={aoGravar}
          depois={
            ficha.licenca_fim !== null && ficha.licenca_dias_para_o_fim !== null ? (
              <p className={PROCEDENCIA}>
                {ficha.licenca_dias_para_o_fim < 0 ? (
                  <>
                    {`A licença terminou há ${inteiro(-ficha.licenca_dias_para_o_fim)} dia(s). O sistema continua funcionando — nada é bloqueado —, e os gestores recebem aviso com a antecedência definida em `}
                    {linkDosPrazos}.
                  </>
                ) : (
                  <>
                    {`Faltam ${inteiro(ficha.licenca_dias_para_o_fim)} dia(s) para o término. Os gestores são avisados com a antecedência definida em `}
                    {linkDosPrazos}
                    {"; nada é bloqueado quando a licença vence."}
                  </>
                )}
              </p>
            ) : undefined
          }
        />
      </div>
      <div className={SEGUINTE_NA_PILHA}>
        <BlocoDosSistemas ficha={ficha} integrado={integrado} aoTrocar={aoTrocarSistemas} />
      </div>
    </div>
  )
}

function LigacaoComOAthus({ ficha }: { ficha: FichaDaEntidade }) {
  return (
    <div className="flex flex-col">
      {ficha.ligacao_com_o_athus.map((canal) => (
        <div key={canal.canal} className="flex items-baseline justify-between gap-4 border-b border-(--borda) py-[9px] last:border-b-0">
          <span className="min-w-[180px] flex-none text-(length:--texto-base) text-(--tinta-2)">{canal.rotulo}</span>
          <span className="text-right font-semibold tabular-nums wrap-anywhere">
            <Selo situacao={canal.configurado ? "ligado" : "desligado"} />
            {!canal.configurado && (
              <span className={PROCEDENCIA}>
                {" · "}
                <ComDica dica="Falta a configuração da ligação com o ATHUS neste sistema. O setor de TI sabe o que preencher">
                  Peça ao setor de TI para configurar
                </ComDica>
              </span>
            )}
            {canal.usado && (
              <span className={PROCEDENCIA}>
                {" · "}
                <ComDica dica="É por aqui que a importação lê os processos e os contratos do ATHUS">Fonte da importação</ComDica>
              </span>
            )}
          </span>
        </div>
      ))}
      {ficha.fonte_do_athus === null && (
        <div className="flex items-baseline justify-between gap-4 py-[9px]">
          <span className="min-w-[180px] flex-none text-(length:--texto-base) text-(--tinta-2)">Fonte da importação</span>
          <span className="text-right font-semibold wrap-anywhere">
            <Selo situacao="desligado" />
            <span className={PROCEDENCIA}>
              {" · "}
              <ComDica dica="A importação precisa saber se lê o banco ou a API do ATHUS. O setor de TI escolhe">
                Peça ao setor de TI para escolher
              </ComDica>
            </span>
          </span>
        </div>
      )}
    </div>
  )
}

function BlocoDosSistemas({ ficha, integrado, aoTrocar }: { ficha: FichaDaEntidade; integrado: boolean; aoTrocar: () => void }) {
  const [catalogo, definirCatalogo] = useState<SistemaDoCatalogo[] | null>(null)
  const [rascunho, definirRascunho] = useState<Record<number, SistemaNoPedido>>({})

  useEffect(() => {
    api
      .catalogoDeSistemas()
      .then(definirCatalogo)
      .catch(() => definirCatalogo([]))
  }, [])

  const abrirRascunho = () => {
    const inicial: Record<number, SistemaNoPedido> = {}
    for (const sistema of ficha.sistemas) {
      inicial[sistema.sistema_id] = {
        sistema_id: sistema.sistema_id,
        situacao: sistema.situacao,
        data: sistema.data,
        observacao: sistema.observacao,
      }
    }
    definirRascunho(inicial)
  }

  const temAthus = ficha.sistemas.some((s) => s.sigla === "ATHUS")

  return (
    <BlocoEditavel
      titulo="Sistemas DATAFull da entidade"
      recado={integrado ? undefined : "A lista vale quando o sistema é integrado."}
      editavel={ficha.pode_editar_licenciamento}
      motivoSemEdicao="só o Root altera o licenciamento"
      leitura={
        <>
          {ficha.sistemas.length === 0 ? (
            <p className={VAZIO}>
              {integrado ? "Nenhum sistema informado ainda. O Root marca aqui os sistemas DATAFull contratados." : "Nenhum sistema informado."}
            </p>
          ) : (
            <Table className={cn(TABELA, "mt-1.5")}>
              <TableHeader>
                <TableRow>
                  <TableHead className={CABECA}>Sistema</TableHead>
                  <TableHead className={CABECA}>Para que serve</TableHead>
                  <TableHead className={CABECA}>Situação</TableHead>
                  <TableHead className={CABECA}>Desde</TableHead>
                  <TableHead className={CABECA}>Observação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={CORPO_LISTRADO}>
                {ficha.sistemas.map((sistema) => (
                  <TableRow key={sistema.sistema_id}>
                    <TableCell className={CELULA_DENSA}>
                      <strong>{sistema.nome}</strong>
                    </TableCell>
                    <TableCell className={cn(COL_OBJETO, MAIUSCULA)}>{sistema.descricao ?? "—"}</TableCell>
                    <TableCell className={cn(CELULA_DENSA, MAIUSCULA)}>{sistema.situacao_rotulo}</TableCell>
                    <TableCell className={CELULA_DENSA}>{data(sistema.data)}</TableCell>
                    <TableCell className={cn(COL_DETALHE, "text-left", MAIUSCULA)}>{sistema.observacao ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-4 mb-1 text-(length:--texto-sm) font-semibold text-(--tinta-2)">
            {temAthus ? "A ligação com o ATHUS neste servidor" : "A ligação com o ATHUS neste servidor (o ATHUS não está na lista acima)"}
          </p>
          <LigacaoComOAthus ficha={ficha} />
        </>
      }
      formulario={() => (
        <FormularioDosSistemas
          catalogo={catalogo ?? []}
          situacoes={ficha.situacoes_do_sistema}
          aoAbrir={abrirRascunho}
          rascunho={rascunho}
          aoMudar={definirRascunho}
        />
      )}
      aoGravar={async () => {
        await api.definirSistemas(ficha.id, Object.values(rascunho))
        aoTrocar()
      }}
    />
  )
}

function FormularioDosSistemas({
  catalogo,
  situacoes,
  aoAbrir,
  rascunho,
  aoMudar,
}: {
  catalogo: SistemaDoCatalogo[]
  situacoes: Opcao[]
  aoAbrir: () => void
  rascunho: Record<number, SistemaNoPedido>
  aoMudar: (novo: Record<number, SistemaNoPedido>) => void
}) {
  useEffect(() => {
    aoAbrir()
  }, [])

  if (catalogo.length === 0) {
    return <p className={VAZIO}>O catálogo de sistemas DATAFull está vazio. O Root o preenche.</p>
  }

  const mudar = (sistemaId: number, parte: Partial<SistemaNoPedido> | null) => {
    const novo = { ...rascunho }
    if (parte === null) {
      delete novo[sistemaId]
    } else {
      novo[sistemaId] = {
        ...(novo[sistemaId] ?? { sistema_id: sistemaId, situacao: "contratado", data: null, observacao: null }),
        ...parte,
      }
    }
    aoMudar(novo)
  }

  return (
    <div className="mt-3 rounded-(--raio-controle) border border-(--borda-forte) bg-(--superficie) p-3.5">
      <Table className={cn(TABELA, "mt-1.5")}>
        <TableHeader>
          <TableRow>
            <TableHead className={CABECA}>Contratado</TableHead>
            <TableHead className={CABECA}>Sistema</TableHead>
            <TableHead className={CABECA}>Situação</TableHead>
            <TableHead className={CABECA}>Data</TableHead>
            <TableHead className={CABECA}>Observação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className={CORPO_LISTRADO}>
          {catalogo.map((sistema) => {
            const marcado = rascunho[sistema.id]
            return (
              <TableRow key={sistema.id}>
                <TableCell className={CELULA_DENSA}>
                  <Checkbox
                    name={`sistema-${sistema.id}`}
                    aria-label={`A entidade tem o ${sistema.nome}`}
                    checked={marcado !== undefined}
                    onCheckedChange={(marcadoAgora) => mudar(sistema.id, marcadoAgora === true ? {} : null)}
                  />
                </TableCell>
                <TableCell className={CELULA_DENSA}>
                  <strong>{sistema.nome}</strong>
                  <div className={cn(PROCEDENCIA, MAIUSCULA)}>{sistema.descricao ?? "—"}</div>
                </TableCell>
                <TableCell className={CELULA_DENSA}>
                  <NativeSelect
                    classNameDaCaixa="w-auto min-w-[130px]"
                    name={`situacao-${sistema.id}`}
                    aria-label={`Situação do ${sistema.nome}`}
                    required={marcado !== undefined}
                    disabled={marcado === undefined}
                    value={marcado?.situacao ?? "contratado"}
                    onChange={(evento) => mudar(sistema.id, { situacao: evento.target.value })}
                  >
                    {situacoes.map((opcao) => (
                      <option key={opcao.chave} value={opcao.chave}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </NativeSelect>
                </TableCell>
                <TableCell className={CELULA_DENSA}>
                  <CampoDeData
                    classeDaCaixa="w-auto min-w-[160px]"
                    name={`data-${sistema.id}`}
                    rotulo={`Data da situação do ${sistema.nome}`}
                    desabilitado={marcado === undefined}
                    valor={marcado?.data ?? ""}
                    aoMudar={(valor) => mudar(sistema.id, { data: valor || null })}
                  />
                </TableCell>
                <TableCell className={CELULA_DENSA}>
                  <Input
                    type="text"
                    name={`observacao-${sistema.id}`}
                    aria-label={`Observação do ${sistema.nome}`}
                    autoComplete="off"
                    data-form-type="other"
                    disabled={marcado === undefined}
                    value={marcado?.observacao ?? ""}
                    onChange={(evento) => mudar(sistema.id, { observacao: evento.target.value || null })}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <p className={PROCEDENCIA}>Desmarcar um sistema o retira da lista, e a retirada fica registrada.</p>
    </div>
  )
}
